import React, { useEffect, useRef } from 'react';
import { Box, makeStyles, IconButton, Tooltip, Typography } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { useTranslation } from 'react-i18next';
import SimpleInfoDialog from '../dialog/SimpleInfoDialog';
import { drawLandCoverChart } from '../../../algorithms/utils/landCoverChart';

/**
 * @function TransectResult
 * @description Displays a single transect's land-cover distribution as a stacked bar chart and
 * table (via Google Charts), plus a plain-language coastal squeeze summary message.
 * @param {Object} props - Component properties.
 * @param {Array<Array>} props.data - Google Charts data table rows: header row followed by one
 * row per land-cover category, with per-year distances.
 * @see {@link getDrawChart}
 * @see {@link getTableChat}
 * @see {@link getSqueezeMessage}
 * **Called by:
 * @see {ProcessingPage} - CSqueeze module's processing page (imported as ProcessingCSqueezePage).
 * @memberof module:Csqueeze
 * @returns {JSX.Element} The transect result summary, chart, and table.
 */
/**
 * How long the panel keeps waiting for Google Charts: `CHART_WAIT_ATTEMPTS` checks
 * `CHART_WAIT_INTERVAL` milliseconds apart, so roughly half a minute before it gives up and
 * leaves the panel without its chart rather than polling for the life of the page.
 * @memberof module:Csqueeze
 */
const CHART_WAIT_INTERVAL = 200;

const CHART_WAIT_ATTEMPTS = 150;

const TransectResult = ({ data }) => {
  const [t] = useTranslation();
  const classes = useStyles();
  const chartsBar = useRef();
  const chartsTable = useRef();
  const [showHelpDialogTransect, setShowHelpDialogTransect] = React.useState(false);

  /**
   * @description Draws the chart and the table once Google Charts is actually loaded.
   * The page asks for the library on mount and it arrives over the network, which used to be long
   * settled by the time anyone finished an analysis and saw this panel. A session import puts the
   * finished analysis on screen immediately, so this ran first and `window.google.visualization`
   * was still undefined - the panel threw, and took the import down with it. What it waits for is
   * every constructor the drawing uses, not just the first thing the loader publishes.
   * Waiting costs nothing
   * in the case that used to work, since the library is already there by then.
   * @memberof module:Csqueeze
   */
  useEffect(() => {
    if (!data || data.length < 2) return undefined;

    let cancelled = false;
    let retry = null;
    let requested = false;
    let attempts = 0;

    /**
     * Every piece the drawing below actually calls. `arrayToDataTable` alone is not enough to go
     * on: the loader registers it before the packages finish arriving, so a panel that checked only
     * for it started drawing while `BarChart` was still undefined - "not a constructor". The bar
     * chart's option converter lives on `charts` rather than `visualization`, and the table is a
     * third package, so all four are asked for by name.
     */
    const ready = () =>
      Boolean(
        window.google?.visualization?.arrayToDataTable &&
          window.google?.visualization?.BarChart &&
          window.google?.visualization?.Table &&
          window.google?.charts?.Bar?.convertOptions,
      );

    const draw = () => {
      if (cancelled || !ready() || !chartsBar.current || !chartsTable.current) return;
      const chartsData = new window.google.visualization.arrayToDataTable([...data]);
      getDrawChart(chartsData, chartsBar.current);
      getTableChat(chartsData, chartsTable.current);
    };

    const whenReady = () => {
      if (cancelled) return;
      if (ready()) {
        draw();
        return;
      }
      const charts = window.google?.charts;
      if (charts?.load && !requested) {
        requested = true;
        const pending = charts.load('current', { packages: ['corechart', 'bar', 'table'] });
        if (pending?.then) pending.then(whenReady);
        else charts.setOnLoadCallback(whenReady);
      }
      attempts += 1;
      if (attempts > CHART_WAIT_ATTEMPTS) return;
      retry = window.setTimeout(whenReady, CHART_WAIT_INTERVAL);
    };

    whenReady();

    return () => {
      cancelled = true;
      if (retry) window.clearTimeout(retry);
    };
  });

  /**
   * @function getTableChat
   * @description Draws the land-cover distribution data as a Google Charts Table.
   * @param {Object} data - Google Charts DataTable.
   * @param {HTMLElement} node - DOM node to draw the table into.
   * **Called by:
   * @see {TransectResult} - via the data-loading effect.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  function getTableChat(data, node) {
    const table = new window.google.visualization.Table(node);
    table.draw(data, {
      showRowNumber: true,
      width: '100%',
      height: '100%',
      responsive: true,
    });
  }

  /**
   * @function getDrawChart
   * @description Draws this transect's land-cover distribution.
   * The drawing is shared with the other coastal squeeze module - see
   * {@link drawLandCoverChart} - because the two measure the same five covers in the same order,
   * and every improvement made here so far had landed in one of them and not the other.
   * @param {Object} data - Google Charts DataTable.
   * @param {HTMLElement} node - DOM node to draw the chart into.
   * **Called by:
   * @see {TransectResult} - via the data-loading effect.
   * @memberof module:Csqueeze
   * @returns {void}
   */
  function getDrawChart(data, node) {
    drawLandCoverChart(data, node, { title: t('forms.csqueeze.result.chartsTitle') });
  }

  /**
   * @function getSqueezeMessage
   * @description Builds a plain-language message describing the coastal squeeze detected for the
   * mangrove land-cover between the first and last year: full squeeze if mangrove distance
   * dropped to zero or below, partial squeeze with a percentage if it decreased, or no squeeze if
   * it held steady or grew.
   * @param {Array<Array>} rows - Google Charts data rows; row 2 is the first year, row 3 the last.
   * **Called by:
   * @see {TransectResult}
   * @memberof module:Csqueeze
   * @returns {String} The localized squeeze summary message.
   */
  function getSqueezeMessage(rows) {
    if (!rows || rows.length < 4 || !rows[2] || !rows[3] || rows[2][3] === undefined) {
      return '';
    }
    let diffMangrove = rows[3][3] - rows[2][3],
      divMangrove = rows[3][3] / rows[2][3],
      squeeze = diffMangrove < 0 ? (divMangrove - 1) * 100 : 0,
      outputtext = '';
    if (rows[2][3] > 0) {
      if (diffMangrove < 0) {
        if (squeeze <= -100) {
          outputtext = t('forms.csqueeze.result.output.fullSqueeze');
        } else {
          outputtext = t('forms.csqueeze.result.output.squeeze').replace('{number}', Math.abs(squeeze.toFixed(2)));
        }
      } else {
        outputtext = t('forms.csqueeze.result.output.noSqueeze');
      }
      outputtext += ` ${t('forms.csqueeze.result.in')} ${parseInt(rows[3][0])} ${t('forms.csqueeze.result.output.thisTransect')}`;
    } else {
      outputtext = t('forms.csqueeze.result.output.noMangrove');
    }
    return outputtext;
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
        <Typography variant="h6" style={{ marginRight: 8 }}>
          {t('forms.csqueeze.result.transectResult')}
        </Typography>
        <Tooltip title={t('forms.csqueeze.result.transectResult')}>
          <IconButton onClick={() => setShowHelpDialogTransect(true)} size="small" style={{ padding: 4 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <SimpleInfoDialog
        open={showHelpDialogTransect}
        close={() => setShowHelpDialogTransect(false)}
        title="forms.csqueeze.result.transectResult"
        text="forms.csqueeze.result.transectResultInfo"
      />
      <Box className={classes.outputtext}>{getSqueezeMessage(data)}</Box>
      <Box style={{ backgroundColor: '#fff', color: '#212121', borderRadius: 8, padding: 8, overflow: 'hidden' }}>
        <div ref={chartsBar} className={classes.chart} style={{ marginLeft: -35 }}></div>
        <div ref={chartsTable} style={{ marginLeft: 0 }}></div>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  /**
   * The chart's own text - the numbers written on the slices, the axis, the title - is drawn on top
   * of the bars as SVG text, and text takes the pointer like anything else. Hovering a slice right
   * where its number is meant hovering the number instead, and the bar underneath never heard about
   * it: the tooltip simply did not come up over the figures the reader is most likely to point at.
   * Letting the pointer through costs nothing - none of that text is there to be clicked.
   */
  chart: {
    '& text': {
      pointerEvents: 'none',
    },
  },
  outputtext: {
    width: '100%',
    height: 'fit-content',
    fontWeight: 'bold',
    fontSize: '1em',
    backgroundColor: 'rgba(128, 128, 128, 0.34)',
    borderRadius: 10,
    padding: '0px 10px',
    display: 'flex',
    alignItems: 'center',
    margin: 'auto',
    placeContent: 'center',
    textAlign: 'center',
  },
}));

export default TransectResult;
